/**
 * The ONE file that reaches v1's promotion slots (DISCOUNT_V2_PLAN.md §9.10, decisions 35, 36 and 45; R2.4e),
 * as `ordering/commit/sharedStores.ts` is the one that reaches v1's sale stores. Deleted at cutover, with
 * the v1 surfaces that read these slots (decision 29). Audit check P refuses any other v2 file importing
 * v1's `DiscountSlice` or `Loyalty` slice.
 *
 * Discount v2 runs inside v1's screens first (D0), and three v1 slots are still read there:
 *
 * | slot | read by | what v2 writes |
 * |---|---|---|
 * | `discountStore.discountApproval` | both apps' carts, Table View, My Orders, both checkouts and engine transports, the approval buttons (state.md §1) | **the approval record's home until cutover (decision 45)**: v1's request button, status wait and cart reset write it too, so v2 READS it here as well (`readApprovalRecord`) and writes it here only |
 * | `LoyaltyStore.externalRefId` | v1's partner release (`helpers/saleHelpers` › `removeExternalDiscount`), when v1's own cart removes a coupon v2 applied | the reference id a v2 apply or verify answered; v2 itself reads the entry's (decision 36) |
 * | `discountStore.discountInfo` | v1's `SaleReturn`, the write path's params, v1's release | a typed code's answer; v2 hands its own to the press that asked (decision 35) |
 *
 * Each mirror dispatches v1's own action, so v1's reducers (and their per-platform differences) decide
 * what lands, exactly as when v1 wrote it.
 */
import store from "../redux-store";
import { addDiscountApprovalData, applyDiscountSuccess } from "../redux-store/slices/DiscountSlice";
import { externalCouponRefId } from "../redux-store/slices/Loyalty";
import type { ApprovalRecord } from "./approval";

type Dispatch = (action: unknown) => unknown;

/** v1's record for a sale (rista-pos-common `DiscountApprovalType`), from v2's. */
export const v1ApprovalRecord = (record: ApprovalRecord) => ({
  mid: record.saleId,
  isApprovalRequired: record.required,
  isRequested: record.requested,
  isApproved: record.approved,
  approvalRequestId: record.requestId ?? "",
  currentStatus: record.status ?? "",
  totalDiscount: record.totalDiscount ?? 0,
  approvalData: record.answer ?? {},
});

/** v2's record from v1's (`approvalRequestId: ""` and `currentStatus: ""` are v1's "none"). */
export const fromV1ApprovalRecord = (record: Record<string, any>): ApprovalRecord => ({
  saleId: String(record?.mid ?? ""),
  required: !!record?.isApprovalRequired,
  requested: !!record?.isRequested,
  approved: !!record?.isApproved,
  ...(record?.approvalRequestId ? { requestId: record.approvalRequestId } : {}),
  ...(record?.currentStatus ? { status: record.currentStatus } : {}),
  ...(record?.totalDiscount !== undefined ? { totalDiscount: Number(record.totalDiscount) } : {}),
  ...(record?.approvalData && Object.keys(record.approvalData).length ? { answer: record.approvalData } : {}),
});

/**
 * A sale's approval record as v1's slot holds it (decision 45), or undefined. v1 keeps one row per sale
 * (rista-pos-common `handleUpdateDiscountApproval` replaces by `mid`); its readers take the first match.
 */
export function readApprovalRecord(state: unknown, saleId: string | undefined): ApprovalRecord | undefined {
  if (!saleId) return undefined;
  const rows: Record<string, any>[] = (state as any)?.discountStore?.discountApproval ?? [];
  const row = rows.find((candidate) => candidate?.mid === saleId);
  return row ? fromV1ApprovalRecord(row) : undefined;
}

export interface V1Slots {
  /** Write a sale's approval record (its home until cutover, decision 45). */
  recordApproval(record: ApprovalRecord): void;
  mirrorReferenceId(referenceId: string): void;
  mirrorCouponAnswer(answer: Record<string, unknown>): void;
  /** A coupon came off: v1's sheet re-applied a stale answer it still held (v1's `dropDiscount` cleared it too). */
  clearCouponAnswer(): void;
}

export function createV1Slots(dispatch: Dispatch): V1Slots {
  return {
    recordApproval: (record) => void dispatch(addDiscountApprovalData(v1ApprovalRecord(record) as never)),
    mirrorReferenceId: (referenceId) => {
      if (referenceId) dispatch(externalCouponRefId(referenceId));
    },
    mirrorCouponAnswer: (answer) => void dispatch(applyDiscountSuccess(answer)),
    clearCouponAnswer: () => void dispatch(applyDiscountSuccess({})),
  };
}

/** The app's slots: the store, read when a mirror runs (never when this module loads). */
export const v1Slots: V1Slots = createV1Slots((action) => store.dispatch(action as never));
